import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState } from 'react';

export default function AppointmentPage() {
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    await fetch('https://readdy.ai/api/form/d7dfbm6m3fsfopf23rjg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-rose-500 via-rose-400 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 to-zinc-950/95"></div>
        <div className="relative z-10 px-8 md:px-16 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-rose-500"></div>
            <span className="text-rose-400 text-xs font-semibold uppercase tracking-[0.25em]">Schedule a Visit</span>
          </div>
          <h1
            className="text-5xl md:text-7xl font-black text-white leading-tight mb-6"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Book an<br /><span className="text-rose-500">Appointment</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl leading-relaxed">
            Same-day and next-day appointments available. New patients always welcome.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-zinc-50 py-16">
        <div className="px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Sidebar */}
            <div className="space-y-5">
              {/* Contact */}
              <div className="bg-white rounded-2xl p-6 border border-zinc-100">
                <h3
                  className="font-black text-zinc-900 mb-5 text-base"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  Contact Us Directly
                </h3>
                {[
                  { icon: 'ri-phone-line', label: 'Phone', value: '(702) 555-0192', href: 'tel:+17025550192' },
                  { icon: 'ri-mail-line', label: 'Email', value: 'info@axispointmedical.com', href: 'mailto:info@axispointmedical.com' },
                ].map((c) => (
                  <div key={c.label} className="flex gap-3 items-center mb-4 last:mb-0">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-rose-50 text-rose-500 shrink-0">
                      <i className={`${c.icon} text-base`}></i>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-400">{c.label}</div>
                      <a href={c.href} className="text-sm font-semibold text-zinc-800 hover:text-rose-500 cursor-pointer transition-colors">{c.value}</a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Hours */}
              <div className="bg-white rounded-2xl p-6 border border-zinc-100">
                <h3
                  className="font-black text-zinc-900 mb-5 text-base"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  Office Hours
                </h3>
                {[
                  { day: 'Monday – Friday', hours: '8:00 AM – 6:00 PM' },
                  { day: 'Saturday', hours: '9:00 AM – 2:00 PM' },
                  { day: 'Sunday', hours: 'Closed' },
                ].map((h) => (
                  <div key={h.day} className="flex justify-between text-sm mb-3 last:mb-0">
                    <span className="text-zinc-500">{h.day}</span>
                    <span className="font-semibold text-zinc-900">{h.hours}</span>
                  </div>
                ))}
              </div>

              {/* Emergency */}
              <div className="bg-zinc-950 rounded-2xl p-6 text-white">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-500/20 mb-4">
                  <i className="ri-alarm-warning-line text-rose-400 text-lg"></i>
                </div>
                <h4
                  className="font-black mb-2 text-base"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  Medical Emergency?
                </h4>
                <p className="text-zinc-400 text-sm leading-relaxed">Please call 911 or go to your nearest emergency room immediately.</p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-8 border border-zinc-100">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-rose-50 text-rose-500 mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h3
                    className="text-2xl font-black text-zinc-900 mb-3"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    Request Received!
                  </h3>
                  <p className="text-zinc-500 text-sm max-w-sm">Thank you! Our team will contact you within 1 business day to confirm your appointment.</p>
                </div>
              ) : (
                <>
                  <h2
                    className="text-2xl font-black text-zinc-900 mb-7"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    Request an Appointment
                  </h2>
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">First Name *</label>
                        <input name="first_name" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="Jane" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Last Name *</label>
                        <input name="last_name" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="Smith" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Email *</label>
                        <input name="email" type="email" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="jane@email.com" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Phone Number *</label>
                        <input name="phone" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="(702) 555-0000" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Date of Birth</label>
                        <input name="date_of_birth" type="date" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Preferred Date</label>
                        <input name="preferred_date" type="date" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Visit Type *</label>
                        <select name="visit_type" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 bg-white cursor-pointer transition-colors">
                          <option value="">Select type</option>
                          <option>New Patient Visit</option>
                          <option>Annual Wellness Exam</option>
                          <option>Sick Visit</option>
                          <option>Follow-Up</option>
                          <option>Telehealth</option>
                          <option>Pediatric Visit</option>
                          <option>Senior Care</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Insurance Plan</label>
                        <select name="insurance" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 bg-white cursor-pointer transition-colors">
                          <option value="">Select plan</option>
                          <option>Medicare Advantage</option>
                          <option>Aetna</option>
                          <option>Blue Cross Blue Shield</option>
                          <option>Cigna</option>
                          <option>Humana</option>
                          <option>UnitedHealthcare</option>
                          <option>Medicaid</option>
                          <option>Self-Pay</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Reason for Visit / Notes</label>
                      <textarea
                        name="notes"
                        rows={4}
                        maxLength={500}
                        className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 resize-none transition-colors"
                        placeholder="Briefly describe your symptoms or reason for visit..."
                        onChange={(e) => setCharCount(e.target.value.length)}
                      />
                      <div className="text-right text-xs text-zinc-400 mt-1">{charCount}/500</div>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-4 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      Submit Appointment Request
                    </button>
                    <p className="text-center text-xs text-zinc-400">By submitting, you agree to our Privacy Policy. We'll contact you to confirm.</p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
